import { TextField } from "@mui/material";
import styles from "../../../../propertydetails.module.css";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { AddLogCallAPI } from "../../../../../../components/AddLogs";
import { toast } from "react-smart-toaster";

const buildSchema = (fieldName) => yup.object().shape({
    [fieldName]: yup.string().url("Please enter a valid URL"),
});

/**
 * Generic gate-unlock-command form. Renders a single labelled command field
 * with Update / Execute actions. Used for both "Gate" and "Gate Door".
 *
 * Props:
 * - label:            Display label, e.g. "Gate" or "Gate Door"
 * - fieldName:         Form/API field name, e.g. "parking_gate_command" or "parking_gate_door_command"
 * - commandValue:      Current saved command value
 * - updateEndpoint:    API path (relative to property) used for the PUT update call
 *                       e.g. "/update-parking-gate-command"
 * - executeEndpoint:   Full API path prefix used for the GET execute call
 *                       e.g. "v1/parking-gate/unlock/"
 */
const PropertyGateCommand = ({
    label,
    fieldName,
    commandValue,
    updateEndpoint,
    executeEndpoint,
    setLoading,
    setSnackbarMsg,
    setSnackbarOpen,
    setPropertyData,
    property_name,
    property_id,
}) => {
    const { getAccessTokenSilently, isAuthenticated, user } = useAuth0();
    const schema = buildSchema(fieldName);
    const { handleSubmit, control, errors, setValue } = useForm({
        shouldUnregister: false,
        resolver: yupResolver(schema),
    });

    const [isEditable, setIsEditable] = useState(false);

    useEffect(() => {
        if (commandValue) {
            setValue(fieldName, commandValue);
        }
    }, [commandValue, fieldName, setValue]);

    const onSubmit = async (data) => {
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
        setLoading(true);
        return axios.put(process.env.REACT_APP_PROPERTY_API_URL + "v1/properties/" + property_id + updateEndpoint, data, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then((response) => {
            setLoading(false);

            setSnackbarMsg(response.data.message);
            setSnackbarOpen(true);

            setPropertyData((prev) => {
                return { ...prev, [fieldName]: response.data?.data?.[fieldName] };
            });

            let logData = {
                title: `Property ${label} unlock command has been updated`,
                description: ["Property: " + property_name],
            };
            AddLogCallAPI(logData, token);

            setIsEditable(false);
        }).catch((error) => {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
            setLoading(false);
        });
    };

    const executeCommand = async () => {
        if (!commandValue) {
            toast.error('Please add command first.');
            return false;
        }
        const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

        setLoading(true);
        return axios.get(process.env.REACT_APP_PROPERTY_API_URL + executeEndpoint + property_id, {
            headers: {
                Authorization: `Bearer ${token}`,
            }
        }).then((response) => {
            setLoading(false);

            setSnackbarMsg(response.data.message);
            setSnackbarOpen(true);
        }).catch((error) => {
            if (typeof error.response !== "undefined")
                toast.error(error.response.data.message);
            setLoading(false);
        });
    };

    return (<form onSubmit={handleSubmit(onSubmit)}>
        <div style={{ maxWidth: "420px", marginBottom: "24px" }}>
            <h6 className={`${styles.SettingsSubTitle}`} style={{ marginBottom: "8px" }}>{label}</h6>
            <div className={`${styles.FormRow}`}>
                <div className={`${styles.FormGroupFullPay} FormGroup`}>
                    <Controller
                        name={fieldName}
                        control={control}
                        render={(field) => (
                            <TextField
                                value={field.value}
                                onChange={(e) => {
                                    field.onChange(e);

                                    if (e.target.value !== commandValue) {
                                        setIsEditable(true);
                                    } else {
                                        setIsEditable(false);
                                    }
                                }}
                                label={`${label} Unlock Command`}
                                variant="outlined"
                            />)}
                    />
                    {errors[fieldName] && (<p className={`${styles.ErrorM}`}>{errors?.[fieldName]?.message}</p>)}
                </div>
            </div>

            {isAuthenticated && user["https://propertydek.com/permissions"].indexOf("property-settings:update") > -1 && <div>
                <button type="submit" className={`${styles.ButtonVoiletSolid}`} disabled={!isEditable}>Update</button>
                <button type="button" className={`${styles.ButtonVoiletSolid}`} onClick={(e) => executeCommand()}>Execute</button>
            </div>}
        </div>
    </form>);
};

export default PropertyGateCommand;